import CourseIcon from "../images/reports/courseIcon.svg?react";
import XIcon from "../images/reports/x-icon.svg?react";
import { Box, IconButton, Typography } from "@mui/material";
import {
	AlphabetImages,
	CoreButton,
	CoreCard,
	CoreModal,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTableColumn,
	CoreTableRow,
	coreToast,
	CoreTreeView,
} from "@verity/components/core";
import { useContext, useEffect, useId, useState } from "react";
import RefreshReportModal from "../admin/report/components/RefreshReport";
import PortalAuthenticationManagerContext, { PortalControllerContext, usePortalStateContext } from "../portal/context";
import VisualizerBanner from "../portal/components/visualizer-banner";
import apiClient from "../helpers/api-client";
import { ThemeWrapper } from "@verity/components";
import i18n from "../i18n";
import { Lms } from "@verity/api-client/types";
import { StatisticsCards, ReportGraph } from "./shared/charts";
import { registerPlugin, fetchLastUpdatedDate, fetchInstitutionReport } from "./shared/helpers";
import { FlagLevelBadge, ReportHeader } from "./shared/report-components";
import { CourseTableEntry, SubAccountNode, ReportResult } from "./shared/report-types";

function CourseTableContainer({ data }: { data?: CourseTableEntry[] }) {
	return (
		<CoreCard useHeader title={i18n.t("admin.components.institutionReportTSX.courseAnalytics")} icon={CourseIcon}>
			<div
				style={{
					fontSize: "15px",
					fontWeight: 400,
					lineHeight: "17.9px",
					textAlign: "left",
					color: "#6B6B6B",
					marginTop: "10px",
					marginBottom: "20px",
				}}
			>
				{i18n.t("admin.components.institutionReportTSX.selectCourseTxt")}
			</div>
			<CourseTable data={data} />
		</CoreCard>
	);
}

function CourseTable({ data }: { data?: CourseTableEntry[] }) {
	const { navigateToPage, changePortalContext } = usePortalStateContext();
	const [courseSearch, setCourseSearch] = useState<string>();
	const controlId = useId();
	const filteredData =
		data?.filter(
			(s) =>
				!courseSearch ||
				courseSearch === "" ||
				s.courseCode.toLowerCase().includes(courseSearch.toLowerCase()) ||
				s.courseName.toLowerCase().includes(courseSearch.toLowerCase()),
		) ?? [];

	const columns: CoreTableColumn<CourseTableEntry>[] = [
		{
			header: i18n.t("admin.components.institutionReportTSX.courseName"),
			key: "courseName",
			cell: (row) => row.data.courseName,
			align: "left",
			truncateString: true,
			descendingComparator: (a, b) => b.courseName.localeCompare(a.courseName),
			width: "20%",
		},
		{
			header: i18n.t("admin.components.institutionReportTSX.courseCode"),
			key: "courseCode",
			cell: (row) => row.data.courseCode,
			align: "left",
			truncateString: true,
			descendingComparator: (a, b) => b.courseCode.localeCompare(a.courseCode),
		},
		{
			header: i18n.t("admin.components.institutionReportTSX.testTakersProctored"),
			key: "studentsProctored",
			cell: (row) => row.data.studentCount,
			align: "left",
			descendingComparator: (a, b) => b.studentCount - a.studentCount,
		},
		{
			header: i18n.t("admin.components.institutionReportTSX.flaggedEvents"),
			key: "flaggedEvents",
			cell: (row) => row.data.flaggedEvents,
			align: "left",
			descendingComparator: (a, b) => b.flaggedEvents - a.flaggedEvents,
		},
		{
			header: i18n.t("admin.components.institutionReportTSX.avgFlagLevel"),
			key: "severityLevel",
			cell: (row) => <FlagLevelBadge flagLevel={row.data.severityLevel} />,
			align: "left",
			descendingComparator: (a, b) => b.severityLevel - a.severityLevel,
		},
		{
			header: i18n.t("admin.components.institutionReportTSX.viewReport"),
			key: "viewReport",
			cell: (row) => (
				<CoreButton
					aria-label={i18n.t("admin.components.institutionReportTSX.viewReportAlt", {
						courseName: row.data.courseName,
					})}
				>
					{i18n.t("admin.components.institutionReportTSX.viewReport")}
				</CoreButton>
			),
			align: "left",
		},
	];
	return (
		<Box display="flex" gap="14px" flexDirection="column" marginY="14px">
			<CoreSearchFieldPaper>
				<CoreSearchField
					label={i18n.t("admin.components.institutionReportTSX.searchByCourseName")}
					onSearch={setCourseSearch}
					totalResults={filteredData.length}
					ariaControls={controlId}
					showSearchButton
					sx={{
						flexGrow: "0.7",
					}}
					value={courseSearch ?? ""}
				/>
			</CoreSearchFieldPaper>
			<CoreTable
				id={controlId}
				loading={data ? false : true}
				columns={columns}
				data={filteredData}
				paginationSize="large"
				rowsPerPage={10}
				allowUIOverrides
				boldHeader={false}
				padding="20px max(22px, min(30px, 1.8vw))"
				onRowClick={async (row: CoreTableRow<CourseTableEntry>) => {
					await changePortalContext({ courseId: row.data.courseId, assessmentId: null });
					navigateToPage("courseReport");
				}}
				noResultsString={i18n.t("admin.components.institutionReportTSX.noResultTxt")}
			/>
		</Box>
	);
}

export function searchSubAccountNodes(nodes: SubAccountNode[], accountName: string): SubAccountNode[] {
	let result: SubAccountNode[] = [];

	for (const node of nodes) {
		if (node.accountName.toLowerCase().includes(accountName.toLowerCase())) {
			result.push(node);
		} else if (node.children.length > 0) {
			const childResults = searchSubAccountNodes(node.children, accountName);
			if (childResults.length > 0) {
				result = result.concat(childResults);
			}
		}
	}
	return result;
}

export function SubAccountFilterModal({
	modalOpen,
	setModalOpen,
	subAccounts,
	setFilteredSubAccountId,
	setFilteredSubaccountName,
	lms,
}: {
	modalOpen: boolean;
	setModalOpen: (open: boolean) => void;
	subAccounts: SubAccountNode[];
	setFilteredSubAccountId: (subAccountId: number | undefined) => void;
	setFilteredSubaccountName: (subaccountName: string | undefined) => void;
	lms: Lms;
}) {
	const [selectedSubaccountId, setSelectedSubaccountId] = useState<number>();
	const [selectedSubaccountName, setSelectedSubaccountName] = useState<string>();
	const [searchText, setSearchText] = useState<string>("");
	const [filteredSubAccounts, setFilteredSubAccounts] = useState<SubAccountNode[]>(subAccounts);
	const controlId = useId();

	interface CustomLabelProps {
		children: string;
		className: string;
		id: number;
	}

	const CustomLabel = (props: CustomLabelProps) => {
		const imageName = props.children
			.replace(/[^a-z]/gi, "")
			.charAt(0)
			.toUpperCase();
		return (
			<div
				style={{
					display: "flex",
					fontSize: "16px",
					alignItems: "center",
					flexBasis: "100%",
				}}
				onClick={() => {
					setSelectedSubaccountId(props.id);
					setSelectedSubaccountName(props.children);
				}}
			>
				<img
					src={AlphabetImages[imageName] ?? AlphabetImages["default"]}
					style={{ marginRight: "8px", width: "24px", height: "24px" }}
					alt={props.children[0]}
				/>
				{props.children}
			</div>
		);
	};

	return (
		<CoreModal
			open={modalOpen}
			onClose={() => setModalOpen(false)}
			widthFixed={true}
			title={[i18n.t("admin.components.institutionReportTSX.select"), i18n.t(`subAccount.singular.${lms}`)].join(" ")}
			minWidth="40%"
			minHeight="60%"
			maxHeight="90%"
			showBottomCloseButton
			buttons={[
				{
					text: i18n.t("admin.components.institutionReportTSX.select"),
					onClick: () => {
						setFilteredSubAccountId(selectedSubaccountId);
						setFilteredSubaccountName(selectedSubaccountName);
						setModalOpen(false);
					},
				},
			]}
		>
			<ThemeWrapper>
				<CoreSearchFieldPaper>
					<CoreSearchField
						label={[
							i18n.t("admin.components.institutionReportTSX.searchBy"),
							i18n.t(`subAccount.singular.${lms}`),
							i18n.t("admin.components.institutionReportTSX.nameOrID"),
						].join(" ")}
						onSearch={(search) => {
							const filteredSubAccounts = searchSubAccountNodes(subAccounts, search);
							setSearchText(search);
							setFilteredSubAccounts(filteredSubAccounts);
						}}
						totalResults={filteredSubAccounts.length}
						ariaControls={controlId}
						value={searchText}
					/>
				</CoreSearchFieldPaper>
				<div
					style={{
						border: "1px solid rgb(224, 224, 224)",
						height: "400px",
						overflow: "auto",
						marginTop: "16px",
						padding: "3px",
					}}
					id={controlId}
				>
					<CoreTreeView
						label={CustomLabel}
						items={filteredSubAccounts ?? []}
						getItemId={(item) => item.id.toString()}
						getItemLabel={(item) => item.accountName + ` (${item.lmsAccountId})`}
					/>
				</div>
			</ThemeWrapper>
		</CoreModal>
	);
}

export function InstitutionReport({ instituteId }: { instituteId: number }) {
	const portalAuthenticationManager = useContext(PortalAuthenticationManagerContext);
	const verityFetch = (input: RequestInfo | URL, init?: RequestInit | undefined) =>
		portalAuthenticationManager?.fetch ? portalAuthenticationManager.fetch(input, init) : fetch(input, init);
	const { portalContext } = useContext(PortalControllerContext);
	const [refreshReportModalOpen, setRefreshReportModalOpen] = useState(false);
	const [reportData, setReportData] = useState<ReportResult>();
	const [lastUpdatedDate, setLastUpdatedDate] = useState<Date>();
	const [showRefresh, setShowRefresh] = useState(false);
	const [liveEnabled, setLiveEnabled] = useState<boolean>();
	const [subAccounts, setSubAccounts] = useState<SubAccountNode[]>();
	const [subAccountFilterModalOpen, setSubAccountFilterModalOpen] = useState(false);
	const [filteredSubAccountId, setFilteredSubAccountId] = useState<number>();
	const [filteredSubaccountName, setFilteredSubaccountName] = useState<string>();
	const lms = portalContext.lms ?? "canvas";
	registerPlugin();

	const [startDate, setStartDate] = useState(() => {
		const date = new Date();
		date.setFullYear(date.getFullYear() - 1);
		date.setDate(1);
		date.setHours(0, 0, 0, 0);
		return date;
	});
	const [endDate, setEndDate] = useState(() => {
		const date = new Date();
		const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
		lastDayOfMonth.setHours(23, 59, 59, 999);
		return lastDayOfMonth;
	});
	useEffect(() => {
		setReportData(undefined);
		const fetchReport = async () => {
			if (startDate > endDate) return;
			const report = await fetchInstitutionReport({
				instituteId,
				subAccountId: filteredSubAccountId,
				startDate,
				endDate,
			});
			if (report) {
				setReportData(report);
			} else {
				coreToast.info(i18n.t("admin.components.institutionReportTSX.longReportTxt"));
			}
		};
		fetchReport().catch(console.error);
	}, [startDate, endDate, filteredSubAccountId]);

	useEffect(() => {
		apiClient.report
			.getSubAccounts()
			.then((subAccounts) => {
				setSubAccounts(subAccounts.subAccounts);
			})
			.catch((e) => {
				setSubAccounts([]);
				console.error(e);
			});
		apiClient.institute
			.isLiveEnabled()
			.then((isLiveEnabled) => {
				setLiveEnabled(isLiveEnabled);
			})
			.catch(console.error);
		fetchLastUpdatedDate(setLastUpdatedDate, verityFetch, instituteId, setShowRefresh).catch(console.error);
	}, []);

	const subAccountBody = filteredSubaccountName ? (
		<Typography fontWeight={700} fontSize={"15px"} color={"#0b6f85"}>
			{filteredSubaccountName}
			<IconButton
				sx={{
					padding: "3px",
					borderRadius: "4px",
					marginLeft: "2px",
					"&:hover": {
						backgroundColor: "#ECECEC",
					},
				}}
				onClick={() => {
					setFilteredSubaccountName(undefined);
					setFilteredSubAccountId(undefined);
				}}
			>
				<XIcon style={{ width: "16px", height: "16px" }} />
			</IconButton>
		</Typography>
	) : (
		<></>
	);

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				background: "#F7F8FA",
				overflow: "auto",
			}}
		>
			<div
				style={{
					width: "100%",
					maxWidth: "100%", // Changed to 100% so that on mobile, children doesn't overflow
					height: "auto",
					minHeight: "100%",
					margin: "0 auto",
				}}
			>
				<ThemeWrapper scoped={false}>
					<Box
						sx={{
							"&:not(:empty)": {
								display: "flex",
								width: "100%",
								maxWidth: "100%",
								padding: "10px",
								paddingTop: "0px",
								marginTop: "10px",
								boxSizing: "border-box",
							},
						}}
					>
						<VisualizerBanner />
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							margin: "10px",
							gap: "14px",
							width: "calc(100% - 20px)", // 20px comes from margin x 2, if you don't subtract, causes overflowing
						}}
					>
						<ReportHeader
							setRefreshReportModalOpen={setRefreshReportModalOpen}
							lastUpdatedDate={lastUpdatedDate}
							startDate={startDate}
							endDate={endDate}
							setStartDate={setStartDate}
							setEndDate={setEndDate}
							type="institution"
							showRefreshButton={showRefresh}
							switchButton={
								subAccounts && subAccounts.length > 0
									? {
											text: [
												filteredSubaccountName
													? i18n.t("admin.components.institutionReportTSX.switch")
													: i18n.t("admin.components.institutionReportTSX.select"),
												i18n.t(`subAccount.singular.${lms}`),
											].join(" "),
											onClick: () => {
												setSubAccountFilterModalOpen(true);
											},
										}
									: undefined
							}
							body={subAccountBody}
							reportInfo={{
								reportType: "institution",
								institutionCourseReportInfo: {
									id: instituteId,
									startDate: startDate,
									endDate: endDate,
									verityFetch: verityFetch,
									institutionInfo: {
										subAccountId: filteredSubAccountId,
										subAccountName: filteredSubaccountName,
									},
								},
							}}
						/>
						<StatisticsCards
							assessments={reportData?.assessments}
							liveSessions={reportData?.liveSessions}
							proctoringHours={reportData?.proctoringHours}
							liveHours={reportData?.liveHours}
							students={reportData?.students}
							veritySessions={reportData?.veritySessions}
							averageSessions={reportData?.averageSessions}
							averageHours={reportData?.averageHours}
							endDate={endDate}
							liveEnabled={liveEnabled}
						/>
						<ReportGraph
							totalVeritySessions={reportData?.monthlyReport?.totalVeritySessions}
							totalLiveSessions={reportData?.monthlyReport?.totalLiveSessions}
							totalLiveCheckInSessions={reportData?.monthlyReport?.totalLiveCheckInSessions}
							totalNonLiveSessions={reportData?.monthlyReport?.totalNonLiveSessions}
							startDateFilter={startDate}
							endDateFilter={endDate}
							type="institution"
						/>
						{(portalContext.lms || (reportData?.courseTableReport && reportData.courseTableReport.length > 1)) && (
							<CourseTableContainer data={reportData?.courseTableReport} />
						)}
						<RefreshReportModal
							instituteId={instituteId}
							handleClose={() => {
								setRefreshReportModalOpen(false);
							}}
							onSubmit={() => {
								setShowRefresh(false);
							}}
							verityFetch={verityFetch}
							open={refreshReportModalOpen}
						/>
					</Box>

					{subAccounts && (
						<SubAccountFilterModal
							modalOpen={subAccountFilterModalOpen}
							setModalOpen={setSubAccountFilterModalOpen}
							subAccounts={subAccounts}
							setFilteredSubAccountId={setFilteredSubAccountId}
							setFilteredSubaccountName={setFilteredSubaccountName}
							lms={lms}
						/>
					)}
				</ThemeWrapper>
			</div>
		</div>
	);
}
